import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useReactRouter from 'use-react-router';
import { useMailchimpConnectionCRUDActions } from '@mm/state/actions/MailchimpConnectionsCRUDActions';
import { useMailchimpCRUDActions } from '@mm/state/actions/MailchimpCRUDActions';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import {
  selectMailchimpAccounts,
  selectMailchimpAccountsId,
} from '@mm/state/selectors/mailchimpSelectors';
import { ButtonColor } from '../../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { useFormSteps } from '../../../../../../../../../common/hooks/use-form-steps';
import { BotBuilderRoutes } from '../../../../../../../../../routes/bot-builder-routes';
import { formStepContainerBuilder } from '../../../../../../../clickToMessengerAds/components/AdConfigDetail/FormStepContainer/FormStepContainer';
import { EditNativeIntegrationHeader } from '../../EditNativeIntegrationHeader/EditNativeIntegrationHeader';
import './EditMailchimp.scss';
import { useEditMailchimp } from './use-edit-mailchimp';
import { useFreeTrialUpgradeModal } from '../../../../../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';

type Props = {
  connectionId?: number;
  isSlideout?: boolean;
  reloadConnections?: () => void;
  onBackClick?: () => void;
};

export const EditMailchimp = (props: Props) => {
  const { isSlideout, connectionId, reloadConnections, onBackClick } = props;
  const { retrieveById, update, deleteConnection } =
    useMailchimpConnectionCRUDActions();
  const { openDeleteConfirmModal, openMailchimpTestConnectionModal } =
    useModalActions();
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const { getAccounts, getLists, getListMergeFields, resetMailchimpStore } =
    useMailchimpCRUDActions();
  const { openFreeTrialUpgradeModalPromotional } = useFreeTrialUpgradeModal();
  const accounts = useSelector(selectMailchimpAccounts);
  const accountsId = useSelector(selectMailchimpAccountsId);

  const [connection, setConnection] = useState<ConnectionModel>();

  const { configs } = useEditMailchimp(setConnection, connection, isSlideout);
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;

  // restore logic
  const {
    history,
    match: {
      params: { mailchimpId },
    },
  } = useReactRouter<{ mailchimpId: string }>();

  useEffect(() => {
    resetMailchimpStore();
    getAccounts(activeFbPageId);
  }, []);

  useEffect(() => {
    const getConnection = async () => {
      const remoteConnection = await retrieveById(
        activeFbPageId,
        isSlideout ? Number(connectionId) : Number(mailchimpId),
      );

      if (remoteConnection) {
        setConnection(remoteConnection);
        if (
          remoteConnection.mailchimpAccessTokenId &&
          accounts.find(
            acc =>
              acc.id === remoteConnection.mailchimpAccessTokenId &&
              !acc.readOnly,
          )
        ) {
          if (accountsId.includes(remoteConnection.mailchimpAccessTokenId)) {
            const remoteLists = await getLists(
              remoteConnection.mailchimpAccessTokenId,
            );
            if (remoteLists && remoteConnection.data.listId) {
              await getListMergeFields(
                remoteConnection.mailchimpAccessTokenId,
                remoteConnection.data.listId,
              );
            }
          }
        }
      }
    };
    getConnection();
  }, []);

  const updateConnection = async (updatedFields: Partial<ConnectionModel>) => {
    if (!connection) {
      return;
    }
    if (!hasNativeIntegrationsFeature) {
      openFreeTrialUpgradeModalPromotional();
      return;
    }
    const updatedConnection = await update(activeFbPageId, {
      ...connection,
      validationMessages: null,
      ...updatedFields,
    });

    if (updatedConnection) {
      setConnection(updatedConnection);
    }
  };

  const onClickActivate = async () => {
    await updateConnection({
      active: true,
    });
  };

  const onClickDelete = () => {
    if (activeFbPageId && connection) {
      const deleteCallback = async () => {
        await deleteConnection(activeFbPageId, connection);
        history.push(
          BotBuilderRoutes.nativeIntegrations.getLink({ chatbotId }),
        );
      };

      openDeleteConfirmModal({
        headerText: 'Are you sure you want to delete this connection?',
        deleteCallback,
        newStyle: true,
      });
    }
  };

  const onClickTestConnection = () => {
    if (connection) {
      openMailchimpTestConnectionModal({ connection, onClickActivate });
    }
  };

  const [nextClicked, setNextClicked] = useState(0);
  const [touchedForms, setTouchedForms] = useState(0);

  const isTouchedStep = useCallback(
    (index: number) => {
      return touchedForms >= index + 1;
    },
    [touchedForms],
  );

  const isCollapsed = (index: number) => {
    if (index === 0) {
      return false;
    }
    return nextClicked < index;
  };

  const [stepsStatus, setStepsStatus] = useFormSteps(
    configs,
    isCollapsed,
    nextClicked,
    true,
  );

  const formSteps = formStepContainerBuilder({
    configs,
    options: {
      className: '',
      contentClassName: isSlideout
        ? 'edit-mailchimp-item__container__slideout'
        : 'edit-mailchimp-item__container',
      isCollapsed,
      isTouched: isTouchedStep,
      isValid: () => true,
      nextAllowed: (index: number) => nextClicked > index,
      onNext: (index: number, allowed?: boolean) => {
        setTouchedForms(Math.max(touchedForms, index + 1));
        setNextClicked(Math.max(touchedForms, index + 1));
      },
      stepsStatus,
      setStepsStatus,
      useCrossIconStyle: true,
      customButtonColor: ButtonColor.blue,
      customOnCollapsibleClicked: (index: number) => {
        setNextClicked(Math.max(touchedForms, index));
      },
    },
  });

  if (!connection) {
    return null;
  }

  return (
    <div className="edit-google-sheet">
      <EditNativeIntegrationHeader
        item={connection}
        helpLink=""
        onToggleActive={() => updateConnection({ active: !connection?.active })}
        onClickDelete={onClickDelete}
        onClickTest={onClickTestConnection}
        onNameUpdate={name => updateConnection({ name })}
        isSlideout={isSlideout}
        reloadConnections={reloadConnections}
        onBackClick={onBackClick}
      />
      {formSteps}
    </div>
  );
};
